/**
 * API calls para policlínicos (tenants) y sus sedes.
 *
 * La ficha del policlínico se lee y se edita en /mi-policlinico; las sedes,
 * en /sedes. Los endpoints /policlinicos —el CRUD de tenants del super-admin—
 * ya no existen: esta instalación es de un policlínico.
 */
import { apiFetch } from "./client";

export interface Policlinico {
  id: number;
  ruc: string;
  slug: string;
  razon_social: string;
  nombre_comercial: string | null;
  regimen_tributario: string;
  plan_codigo: string;
  color_primario: string | null;
  /** Segundo color del logotipo (el rojo de "POLICLÍNICO" en Mi Salud). */
  color_secundario: string | null;
  lema: string | null;
  has_logo: boolean;
  /** Zona IANA. Las horas de agenda y citas se leen en esta zona. */
  zona_horaria: string;
  /** Dominio propio. Con valor, ese host entra sin slug en la URL. */
  dominio: string | null;
  /** Solo las excepciones escritas a mano, no lo efectivo. Ver PoliclinicoUpdate. */
  funcionalidades: Record<string, boolean>;
  activo: boolean;
  created_at: string;
}

export interface PoliclinicoCreate {
  ruc: string;
  razon_social: string;
  nombre_comercial?: string | null;
  regimen_tributario?: string;
  plan_codigo?: string;
  slug?: string | null;
  color_primario?: string | null;
  color_secundario?: string | null;
  lema?: string | null;
}

export interface PoliclinicoUpdate {
  razon_social?: string;
  nombre_comercial?: string | null;
  regimen_tributario?: string;
  plan_codigo?: string;
  slug?: string;
  color_primario?: string | null;
  color_secundario?: string | null;
  lema?: string | null;
  zona_horaria?: string;
  dominio?: string | null;
  /**
   * Módulos encendidos o apagados POR ENCIMA del plan. `{"farmacia": false}`
   * apaga lo que el plan traía; `{"auditoria": true}` enciende lo que no.
   * Lo que no está escrito lo decide el plan.
   */
  funcionalidades?: Record<string, boolean>;
  activo?: boolean;
}

export interface Sede {
  id: number;
  nombre: string;
  ubigeo: string;
  direccion: string;
  telefono: string | null;
  categoria_minsa: string;
  activo: boolean;
  created_at: string;
}

export interface SedeCreate {
  nombre: string;
  ubigeo: string;
  direccion: string;
  telefono?: string | null;
  categoria_minsa: string;
}

export interface SedeUpdate {
  nombre?: string;
  direccion?: string;
  telefono?: string | null;
  categoria_minsa?: string;
  activo?: boolean;
}

// ---------- Policlínicos ----------

export interface TenantEspecialidad {
  especialidad_id: number;
  codigo: string;
  nombre: string;
  activa: boolean;
}

export interface Especialidad {
  id: number;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  /** Colegios profesionales que la ejercen (CMP, COP, CObP…). */
  profesiones: string[];
}

export interface EspecialidadCreatePayload {
  codigo: string;
  nombre: string;
  descripcion?: string | null;
  /** Colegios que la ejercen. Obligatorio: sin ellos nadie puede asignársela. */
  profesiones: string[];
}

export interface EspecialidadUpdatePayload {
  codigo?: string;
  nombre?: string;
  descripcion?: string | null;
  profesiones?: string[];
}

export function listEspecialidadesCatalogo(): Promise<Especialidad[]> {
  return apiFetch<Especialidad[]>("/v1/especialidades");
}

export function createEspecialidad(payload: EspecialidadCreatePayload): Promise<Especialidad> {
  return apiFetch<Especialidad>("/v1/especialidades", {
    method: "POST",
    body: payload,
  });
}

export function updateEspecialidad(
  id: number,
  payload: EspecialidadUpdatePayload,
): Promise<Especialidad> {
  return apiFetch<Especialidad>(`/v1/especialidades/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

// ---------- Ajustes del propio policlínico (admin del tenant) ----------

export interface MiPoliclinico {
  id: number;
  ruc: string;
  slug: string;
  razon_social: string;
  nombre_comercial: string | null;
  regimen_tributario: string;
  plan_codigo: string;
  color_primario: string | null;
  color_secundario: string | null;
  lema: string | null;
  has_logo: boolean;
  zona_horaria: string;
  activo: boolean;
}

/** Solo lo que el admin del policlínico puede cambiar de su tenant. */
export interface MiPoliclinicoUpdate {
  nombre_comercial?: string | null;
  color_primario?: string | null;
  color_secundario?: string | null;
  lema?: string | null;
  zona_horaria?: string;
}

export function getMiPoliclinico(): Promise<MiPoliclinico> {
  return apiFetch<MiPoliclinico>("/v1/mi-policlinico");
}

export function updateMiPoliclinico(payload: MiPoliclinicoUpdate): Promise<MiPoliclinico> {
  return apiFetch<MiPoliclinico>("/v1/mi-policlinico", { method: "PATCH", body: payload });
}

/**
 * El catálogo de módulos, para pintar los interruptores de la ficha.
 *
 * Es una copia del de `app/core/funcionalidades.py`, y lo es a sabiendas: son
 * ocho cadenas que cambian una vez al año, y montar un endpoint para servirlas
 * es más piezas que mantener que las dos líneas que se tocan aquí el día que
 * se añada un módulo. Quien manda sigue siendo el backend — si aquí sobra un
 * código, el PATCH lo rechaza con un 422 que lo dice por su nombre.
 */
export const CATALOGO_FUNCIONALIDADES: { codigo: string; label: string }[] = [
  { codigo: "farmacia", label: "Farmacia" },
  { codigo: "laboratorio", label: "Laboratorio" },
  { codigo: "facturacion_electronica", label: "Facturación electrónica" },
  { codigo: "portal_paciente", label: "Portal del paciente" },
  { codigo: "planes_tratamiento", label: "Planes de tratamiento" },
  { codigo: "apoderados", label: "Apoderados de menores" },
  { codigo: "auditoria", label: "Registro de accesos" },
  { codigo: "app_propia", label: "App móvil propia" },
  { codigo: "dominio_propio", label: "Dominio propio" },
];

/**
 * Las sedes del policlínico.
 *
 * Antes había dos: esta y `listSedesOfTenant`, que pedía
 * `/policlinicos/{id}/sedes` y solo funcionaba con la cuenta del super-admin.
 * Al desaparecer los tenants, la segunda deja de tener sentido y queda una.
 */
export function listSedes(): Promise<Sede[]> {
  return apiFetch<Sede[]>("/v1/sedes");
}

export function createSede(payload: SedeCreate): Promise<Sede> {
  return apiFetch<Sede>("/v1/sedes", { method: "POST", body: payload });
}

export function updateSede(sedeId: number, payload: SedeUpdate): Promise<Sede> {
  return apiFetch<Sede>(`/v1/sedes/${sedeId}`, { method: "PATCH", body: payload });
}
